import { EllipsisVertical } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export interface MenuItem {
  label: string;
  onSelect: () => void;
  danger?: boolean;
  testId?: string;
  hidden?: boolean;
  /** v2.10: a link item (e.g. `tel:`) — rendered as an <a>, so a phone's own handler opens it. */
  href?: string;
}

/**
 * v2.9 P2.1: the ⋯ menu on every note and call-log entry. Edit lives here, not on a click of the text —
 * a click on the body only ever follows a link. Closes on Escape, on an outside click and on a pick.
 */
export function ActionMenu({ items, testId }: { items: MenuItem[]; testId: string }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);
  const shown = items.filter((i) => !i.hidden);
  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={testId}
        className="rounded p-0.5 text-fg-muted hover:bg-surface-2 hover:text-fg pointer-coarse:min-h-11 pointer-coarse:min-w-11 pointer-coarse:inline-flex pointer-coarse:items-center pointer-coarse:justify-center"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <EllipsisVertical className="size-3.5" aria-hidden />
      </button>
      {open && (
        <ul
          role="menu"
          className="absolute right-0 z-40 mt-1 w-56 rounded-[var(--radius-card)] border bg-surface py-1 text-xs shadow-lg"
        >
          {shown.map((item) => (
            <li key={item.label} role="none">
              {item.href ? (
                <a
                  role="menuitem"
                  href={item.href}
                  data-testid={item.testId}
                  className="block w-full px-3 py-2 text-left hover:bg-surface-2 pointer-coarse:py-3"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    item.onSelect();
                  }}
                >
                  {item.label}
                </a>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  data-testid={item.testId}
                  className={
                    "block w-full px-3 py-1.5 text-left hover:bg-surface-2 pointer-coarse:py-3" +
                    (item.danger ? " text-danger" : "")
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    item.onSelect();
                  }}
                >
                  {item.label}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
